/**
 * @file ScribingStep.tsx
 *
 * @description Step component for the Miracle Flow scribing exercise.
 * Provides a textarea for free-form writing and reflection.
 *
 * @module miracle-flow
 *
 * @domain productivity
 *
 */

import { PenTool } from "lucide-react";
import React from "react";

import { Stack, Heading, Textarea } from "@/components/ui";
import { cn } from "@/shared/utils";

/**
 * Props for ScribingStep component
 */
export interface ScribingStepProperties {
  className?: string;
  onChange: (value: string) => void;
  value: string;
}

/**
 * ScribingStep - Free-form writing step for Miracle Flow
 *
 * Features:
 * - Large textarea for free-form writing
 * - Encourages unstructured thought flow
 * - Hero section with icon and description
 * - Minimal constraints for creative expression
 *
 * @param root0
 *
 * @param root0.value
 *
 * @param root0.onChange
 *
 * @param root0.className
 *
 * @example
 * ```tsx
 * <ScribingStep
 *   value={scribingText}
 *   onChange={setScribingText}
 * />
 * ```
 *
 * @returns JSX.Element
 *
 */
export const ScribingStep: React.FC<ScribingStepProperties> = ({ className, onChange, value }) => {
  return (
    <div className={cn("space-y-6 w-full", className)}>
      {/* Hero Section */}
      <Stack align="center" className="py-4 w-full" direction="col" gap={4}>
        <div className="flex items-center justify-center w-20 h-20 rounded-tracq bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-tracq-card">
          <PenTool className="w-10 h-10 text-white" />
        </div>
        <Stack align="center" className="w-full max-w-4xl" direction="col" gap={2}>
          <Heading className="font-bold text-center text-indigo-400" level="h3" size="xl">
            Scribing
          </Heading>
          <p className="text-[#CBD5E1] text-center text-lg w-full px-4">
            Write down your thoughts, goals, or anything that&apos;s on your mind. Let your thoughts
            flow freely.
          </p>
        </Stack>
      </Stack>

      {/* Scribing Textarea */}
      <div className="space-y-3 w-full">
        <label className="block text-sm font-semibold text-[#CBD5E1]">Your Thoughts</label>
        <Textarea
          className="bg-[#334155] border-[#475569] text-[#F1F5F9] placeholder:text-[#94A3B8] min-h-[250px] text-base leading-relaxed w-full"
          onChange={(value: string) => {
            onChange(value);
          }}
          placeholder="What's on your mind? Write freely about your goals, thoughts, plans, or anything you want to capture. This is your space to reflect and plan..."
          rows={10}
          value={value}
        />
        <p className="text-[#94A3B8] text-xs">
          Don&apos;t worry about structure or perfection. Just let your thoughts flow onto the page.
        </p>
      </div>
    </div>
  );
};
